/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useRef } from 'react';
import './Navbar.css';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FeatureFlag, useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { useTranslation } from 'react-i18next';
import logo from '../../assets/logo.png';
import SearchBar from '../SearchBar/SearchBar';
import { motion, AnimatePresence } from 'framer-motion';
import {
  useSafeTwilioConversations,
  type AppConversation,
} from '../../contexts/twilioConversationContext.shared';
import {
  User,
  PawPrint,
  Calendar,
  Heart,
  FileText,
  NotebookText,
  MessageCircle,
  LogOut,
  Briefcase,
  Package,
  Repeat,
  Coins,
  Gift,
  GraduationCap,
  Brain,
  Home,
  Footprints,
  Scissors,
  HeartPulse,
  BedDouble,
  Salad,
  Leaf,
  Trophy,
  HandHeart,
  Dumbbell,
  Flower2,
  Car,
  Camera,
  Bandage,
  Ticket,
} from 'lucide-react';
import { METIERS_DATA } from '../../pages/MetierLanding/metiersData';
import { getLocaleFromPath, localeHubPath } from '../../config/localeRoutes';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import { buildApiUrl } from '../../config/apiConfig';
import {
  CATALOG_EVENTS_SEARCH_PATH,
  NAV_CATALOG_TABS,
  catalogServiceSearchPath,
  parseCatalogServices,
  type CatalogService,
  type NavCatalogTab,
} from './navCatalog';

const METIER_ICON: Record<string, React.ElementType> = {
  'educateur-canin': GraduationCap,
  'comportementaliste-canin': Brain,
  'pet-sitter': Home,
  'promeneur-canin': Footprints,
  'toiletteur-canin': Scissors,
  'osteopathe-canin': HeartPulse,
  'pension-canine': BedDouble,
  'nutritionniste-animal': Salad,
  'communication-animale': Leaf,
  'loisir-canin': Trophy,
  'massage-animalier': HandHeart,
  'sport-canin': Dumbbell,
  'naturopathe': Flower2,
  'taxi-animalier': Car,
  'photographe-animalier': Camera,
  'secourisme-animalier': Bandage,
};

function isLandingPath(pathname: string): boolean {
  const current = pathname.replace(/\/$/, '') || '/';
  if (current === '/') return true;
  const pathLocale = getLocaleFromPath(pathname);
  if (!pathLocale) return false;
  const hub = localeHubPath(pathLocale).replace(/\/$/, '') || '/';
  return current === hub;
}

function metierNavLabel(
  slug: string,
  t: (key: string) => string,
  exists: (key: string) => boolean
): string {
  const key = `metierLabels.${slug}`;
  if (exists(key)) return t(key);
  const singular = METIERS_DATA[slug]?.singular;
  if (!singular) return slug;
  return singular.charAt(0).toUpperCase() + singular.slice(1);
}

const METIER_SLUGS = Object.keys(METIERS_DATA);

let catalogServicesPromise: Promise<CatalogService[]> | null = null;

function loadCatalogServices(): Promise<CatalogService[]> {
  if (!catalogServicesPromise) {
    catalogServicesPromise = fetch(buildApiUrl('/get-services'))
      .then(response => (response.ok ? response.json() : { services: [] }))
      .then(payload => parseCatalogServices(payload))
      .catch(() => {
        catalogServicesPromise = null;
        return [];
      });
  }
  return catalogServicesPromise;
}

function CatalogTabs({
  tab,
  onChange,
}: {
  tab: NavCatalogTab;
  onChange: (next: NavCatalogTab) => void;
}) {
  const { t } = useTranslation('navigation');
  return (
    <div className='nav-services-tabs' role='tablist'>
      {NAV_CATALOG_TABS.map(item => (
        <button
          key={item.id}
          type='button'
          role='tab'
          aria-selected={tab === item.id}
          className={`nav-services-tab${tab === item.id ? ' nav-services-tab--active' : ''}`}
          onClick={() => onChange(item.id)}
        >
          {t(`catalogTabs.${item.id}`)}
        </button>
      ))}
    </div>
  );
}

const Navbar: React.FC = () => {
  const { t, i18n } = useTranslation('navigation');
  const location = useLocation();
  const showNavbarSearch = !isLandingPath(location.pathname);
  const animalLinkState = { from: `${location.pathname}${location.search}` };
  const { user, isAuthenticated, logout } = useAuth();
  const { isFeatureEnabled } = useFeatureFlags();
  const showFavorites = isFeatureEnabled(FeatureFlag.FAVORITES);
  const showMessaging = isFeatureEnabled(FeatureFlag.CHAT_FEATURE);
  const showReports = isFeatureEnabled(FeatureFlag.CLIENT_REPORTS);
  const showQuotes = isFeatureEnabled(FeatureFlag.QUOTES);
  const showForfaits = isFeatureEnabled(FeatureFlag.FORFAITS);
  const showClub = isFeatureEnabled(FeatureFlag.CLUB_SUBSCRIPTIONS);
  const showCredits = isFeatureEnabled(FeatureFlag.CREDITS);
  const [isFloating, setIsFloating] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [catalogTab, setCatalogTab] = useState<NavCatalogTab>('professions');
  const [catalogServices, setCatalogServices] = useState<CatalogService[]>([]);
  const servicesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!servicesOpen && !isExpanded) return;
    let cancelled = false;
    loadCatalogServices().then(rows => {
      if (!cancelled) setCatalogServices(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [servicesOpen, isExpanded]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const originalTitleRef = useRef<string | null>(null);

  const twilioContext = useSafeTwilioConversations();

  useEffect(() => {
    const handleScroll = () => {
      setIsFloating(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (twilioContext && Array.isArray(twilioContext.conversations)) {
      let totalUnread = 0;

      for (let i = 0; i < twilioContext.conversations.length; i++) {
        const conv = twilioContext.conversations[i];
        totalUnread += conv.unreadMessagesCount || 0;
      }

      setUnreadMessages(totalUnread);
    } else {
      setUnreadMessages(0);
    }
  }, [twilioContext]);

  useEffect(() => {
    if (originalTitleRef.current === null) {
      // Nettoyer le titre au cas où il contient déjà une notification (ex: après un refresh)
      originalTitleRef.current = document.title.replace(/^\(\d+\)\s*/, '');
    }

    if (unreadMessages > 0) {
      document.title = `(${unreadMessages}) ${originalTitleRef.current}`;
    } else {
      if (originalTitleRef.current) {
        document.title = originalTitleRef.current;
      }
    }

    return () => {
      if (originalTitleRef.current) {
        document.title = originalTitleRef.current;
      }
    };
  }, [unreadMessages]);

  useEffect(() => {
    if (twilioContext && typeof twilioContext.refreshUnreadMessagesCount === 'function') {
      twilioContext.refreshUnreadMessagesCount();

      const intervalId = setInterval(() => {
        twilioContext.refreshUnreadMessagesCount();
      }, 15000);

      return () => {
        clearInterval(intervalId);
      };
    }
  }, [twilioContext]);

  const toggleMenu = () => {
    setIsExpanded(prev => !prev);
    setServicesOpen(false);
  };

  const menuVariants = {
    closed: {
      height: 0,
      opacity: 0,
      transition: {
        duration: 0.3,
        when: 'afterChildren',
      },
    },
    open: {
      height: 'auto',
      opacity: 1,
      transition: {
        duration: 0.3,
        when: 'beforeChildren',
        staggerChildren: 0.1,
      },
    },
  };

  const menuItemVariants = {
    closed: {
      y: -20,
      opacity: 0,
    },
    open: {
      y: 0,
      opacity: 1,
    },
  };

  return (
    <nav className={`navbar ${isFloating ? 'floating' : 'normal'}`}>
      <div className='navbar__start'>
        <div className='logo'>
          <Link
            to='/'
            style={{ textDecoration: 'none', color: 'inherit' }}
            aria-label={t('homeAria')}
          >
            <motion.img
              src={logo}
              alt=''
              aria-hidden
              className='logo-image'
              whileHover={{ scale: 1.05 }}
              transition={{ type: 'spring', stiffness: 400, damping: 10 }}
            />
          </Link>
        </div>

        {/* Desktop — menu Services déroulant */}
        <div className='nav-services-menu' ref={servicesRef}>
          <button
            className={`nav-services-trigger${servicesOpen ? ' nav-services-trigger--open' : ''}${METIER_SLUGS.some(s => location.pathname === `/services/${s}`) ? ' nav-services-trigger--active' : ''}`}
            onClick={() => setServicesOpen(o => !o)}
            aria-expanded={servicesOpen}
            aria-haspopup='true'
          >
            {t('servicesMenu')}
            <svg className='nav-services-chevron' viewBox='0 0 16 16' fill='none' aria-hidden>
              <path
                d='M4 6l4 4 4-4'
                stroke='currentColor'
                strokeWidth='1.75'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </button>

          {servicesOpen && (
            <div className='nav-services-dropdown'>
              <CatalogTabs tab={catalogTab} onChange={setCatalogTab} />
              {catalogTab === 'professions' && (
                <div className='nav-services-grid' role='menu'>
                  {METIER_SLUGS.map(slug => {
                    const Icon = METIER_ICON[slug];
                    const path = `/services/${slug}`;
                    const isActive = location.pathname === path;
                    return (
                      <Link
                        key={slug}
                        to={path}
                        role='menuitem'
                        className={`nav-services-item${isActive ? ' nav-services-item--active' : ''}`}
                        onClick={() => setServicesOpen(false)}
                      >
                        {Icon && <Icon size={15} className='nav-services-item__icon' />}
                        {metierNavLabel(slug, t, i18n.exists)}
                      </Link>
                    );
                  })}
                </div>
              )}
              {catalogTab === 'services' && (
                <div className='nav-services-grid' role='menu'>
                  {catalogServices.map(service => (
                    <Link
                      key={service.id}
                      to={catalogServiceSearchPath(service.name)}
                      role='menuitem'
                      className='nav-services-item'
                      onClick={() => setServicesOpen(false)}
                    >
                      {service.name}
                    </Link>
                  ))}
                  {catalogServices.length === 0 && (
                    <p className='nav-services-empty'>{t('noServicesToShow')}</p>
                  )}
                </div>
              )}
              {catalogTab === 'events' && (
                <Link
                  to={CATALOG_EVENTS_SEARCH_PATH}
                  className='nav-services-events'
                  onClick={() => setServicesOpen(false)}
                >
                  <Ticket size={18} className='nav-services-item__icon' />
                  <span>
                    <strong>{t('allEvents')}</strong>
                    <small>{t('allEventsHint')}</small>
                  </span>
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {showNavbarSearch && (
        <div className='navbar-search'>
          <SearchBar withMarginTop={false} showGuidance={false} idPrefix='navbar-search' />
        </div>
      )}

      <motion.div
        className='menu-icon'
        onClick={toggleMenu}
        animate={{ rotate: isExpanded ? 90 : 0 }}
        transition={{ duration: 0.3 }}
      >
        ☰
      </motion.div>

      <div className='navbar__actions'>
        {showFavorites && isAuthenticated && (
          <Link
            to='/favorites'
            className='navbar-favorites-btn'
            title={t('menu.favorites')}
            aria-label={t('menu.favorites')}
          >
            <Heart size={20} />
          </Link>
        )}
        <LanguageSwitcher />
        <div className='buttons'>
          {!isAuthenticated && (
            <a href='https://www.rintintin-pro.com' target='_blank' rel='noopener noreferrer'>
              <button className='btn outline'>{t('iAmAPro')}</button>
            </a>
          )}
          {isAuthenticated ? (
            <div className='user-menu'>
              {/* Quick access icons - Desktop only */}
              {showFavorites && (
                <Link to='/favorites' className='quick-access-icon' title={t('menu.favorites')}>
                  <Heart size={20} />
                </Link>
              )}
              {showMessaging && (
                <Link to='/messaging' className='quick-access-icon' title={t('menu.messages')}>
                  <MessageCircle size={20} />
                  {unreadMessages > 0 && <span className='notification-badge'>{unreadMessages}</span>}
                </Link>
              )}

              <img
                src={
                  user?.profile_photo_url ||
                  'https://img.freepik.com/vecteurs-premium/photo-profil-avatar-homme-illustration-vectorielle_268834-538.jpg'
                }
                alt='Profile'
                className='profile-pic'
              />
              <div className='dropdown'>
                <button className='dropbtn'>☰</button>
                <div className='dropdown-content'>
                  <div className='menu-group'>
                    <div className='menu-group-title'>{t('menuGroupAccount')}</div>
                    <Link to='/profile'>
                      <User className='menu-item-icon' />
                      {t('menuProfile')}
                    </Link>
                    <Link to='/animal' state={animalLinkState}>
                      <PawPrint className='menu-item-icon' />
                      {t('menuAnimals')}
                    </Link>
                  </div>

                  <div className='menu-group'>
                    <div className='menu-group-title'>{t('menu.myActivities')}</div>
                    <Link to='/reservations'>
                      <Calendar className='menu-item-icon' />
                      {t('menuReservations')}
                    </Link>
                    {showReports && (
                      <Link to='/mes-suivis'>
                        <NotebookText className='menu-item-icon' />
                        {t('menu.myReports')}
                      </Link>
                    )}
                    {showQuotes && (
                      <Link to='/quotes'>
                        <FileText className='menu-item-icon' />
                        {t('menuQuotes')}
                      </Link>
                    )}
                  </div>

                  <div className='menu-group'>
                    <div className='menu-group-title'>{t('menuGroupPerks')}</div>
                    {showForfaits && (
                      <Link to='/mes-forfaits'>
                        <Package className='menu-item-icon' />
                        {t('menuForfaits')}
                      </Link>
                    )}
                    {showClub && (
                      <Link to='/mes-abonnements'>
                        <Repeat className='menu-item-icon' />
                        {t('menuSubscriptions')}
                      </Link>
                    )}
                    {showCredits && (
                      <Link to='/credits'>
                        <Coins className='menu-item-icon' />
                        {t('menuCredits')}
                      </Link>
                    )}
                    <Link to='/mes-cadeaux'>
                      <Gift className='menu-item-icon' />
                      {t('menuGiftCodes')}
                    </Link>
                  </div>

                  <div className='menu-group'>
                    <button onClick={() => logout()} className='text-red-500'>
                      <LogOut className='menu-item-icon' />
                      {t('menuLogout')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <Link to='/login' state={{ from: `${location.pathname}${location.search}` }}>
              <button className='btn filled'>{t('login')}</button>
            </Link>
          )}
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            className='menu-content'
            initial='closed'
            animate='open'
            exit='closed'
            variants={menuVariants}
          >
            {!isAuthenticated && (
              <motion.div className='menu-item login-cta' variants={menuItemVariants}>
                <Link
                  to='/login'
                  state={{ from: `${location.pathname}${location.search}` }}
                  onClick={toggleMenu}
                >
                  <User className='menu-item-icon' />
                  {t('login')}
                </Link>
              </motion.div>
            )}

            <div className='menu-group'>
              <div className='menu-group-title'>{t('servicesMenu')}</div>
              <CatalogTabs tab={catalogTab} onChange={setCatalogTab} />
              {catalogTab === 'professions' &&
                METIER_SLUGS.map(slug => {
                  const Icon = METIER_ICON[slug];
                  return (
                    <motion.div key={slug} className='menu-item' variants={menuItemVariants}>
                      <Link to={`/services/${slug}`} onClick={toggleMenu}>
                        {Icon && <Icon className='menu-item-icon' />}
                        {metierNavLabel(slug, t, i18n.exists)}
                      </Link>
                    </motion.div>
                  );
                })}
              {catalogTab === 'services' &&
                catalogServices.map(service => (
                  <motion.div key={service.id} className='menu-item' variants={menuItemVariants}>
                    <Link to={catalogServiceSearchPath(service.name)} onClick={toggleMenu}>
                      {service.name}
                    </Link>
                  </motion.div>
                ))}
              {catalogTab === 'events' && (
                <motion.div className='menu-item' variants={menuItemVariants}>
                  <Link to={CATALOG_EVENTS_SEARCH_PATH} onClick={toggleMenu}>
                    <Ticket className='menu-item-icon' />
                    {t('allEvents')}
                  </Link>
                </motion.div>
              )}
            </div>

            {isAuthenticated ? (
              <>
                {/* Groupe PRO */}
                <div className='menu-group'>
                  <motion.div className='menu-item' variants={menuItemVariants}>
                    <a
                      href='https://www.rintintin-pro.com'
                      target='_blank'
                      rel='noopener noreferrer'
                      onClick={toggleMenu}
                    >
                      <Briefcase className='menu-item-icon' />
                      {t('iAmAPro')}
                    </a>
                  </motion.div>
                </div>

                {/* Groupe MON COMPTE */}
                <div className='menu-group'>
                  <div className='menu-group-title'>{t('menu.myAccount')}</div>
                  <motion.div className='menu-item' variants={menuItemVariants}>
                    <Link to='/profile' onClick={toggleMenu}>
                      <User className='menu-item-icon' />
                      {t('menu.myProfile')}
                    </Link>
                  </motion.div>
                  <motion.div className='menu-item' variants={menuItemVariants}>
                    <Link to='/animal' state={animalLinkState} onClick={toggleMenu}>
                      <PawPrint className='menu-item-icon' />
                      {t('menu.myAnimals')}
                    </Link>
                  </motion.div>
                  {showFavorites && (
                    <motion.div className='menu-item' variants={menuItemVariants}>
                      <Link to='/favorites' onClick={toggleMenu}>
                        <Heart className='menu-item-icon' />
                        {t('menu.favorites')}
                      </Link>
                    </motion.div>
                  )}
                </div>

                {/* Groupe MES ACTIVITÉS */}
                <div className='menu-group'>
                  <div className='menu-group-title'>{t('menu.myActivities')}</div>
                  <motion.div className='menu-item' variants={menuItemVariants}>
                    <Link to='/reservations' onClick={toggleMenu}>
                      <Calendar className='menu-item-icon' />
                      {t('menuReservations')}
                    </Link>
                  </motion.div>
                  {showReports && (
                    <motion.div className='menu-item' variants={menuItemVariants}>
                      <Link to='/mes-suivis' onClick={toggleMenu}>
                        <NotebookText className='menu-item-icon' />
                        {t('menu.myReports')}
                      </Link>
                    </motion.div>
                  )}
                  {showQuotes && (
                    <motion.div className='menu-item' variants={menuItemVariants}>
                      <Link to='/quotes' onClick={toggleMenu}>
                        <FileText className='menu-item-icon' />
                        {t('menuQuotes')}
                      </Link>
                    </motion.div>
                  )}
                </div>

                {/* Groupe AVANTAGES */}
                <div className='menu-group'>
                  <div className='menu-group-title'>{t('menuGroupPerks')}</div>
                  {showForfaits && (
                    <motion.div className='menu-item' variants={menuItemVariants}>
                      <Link to='/mes-forfaits' onClick={toggleMenu}>
                        <Package className='menu-item-icon' />
                        {t('menuForfaits')}
                      </Link>
                    </motion.div>
                  )}
                  {showClub && (
                    <motion.div className='menu-item' variants={menuItemVariants}>
                      <Link to='/mes-abonnements' onClick={toggleMenu}>
                        <Repeat className='menu-item-icon' />
                        {t('menuSubscriptions')}
                      </Link>
                    </motion.div>
                  )}
                  {showCredits && (
                    <motion.div className='menu-item' variants={menuItemVariants}>
                      <Link to='/credits' onClick={toggleMenu}>
                        <Coins className='menu-item-icon' />
                        {t('menuCredits')}
                      </Link>
                    </motion.div>
                  )}
                  <motion.div className='menu-item' variants={menuItemVariants}>
                    <Link to='/mes-cadeaux' onClick={toggleMenu}>
                      <Gift className='menu-item-icon' />
                      {t('menuGiftCodes')}
                    </Link>
                  </motion.div>
                </div>

                {/* Groupe LOGOUT */}
                <div className='menu-group'>
                  <motion.div
                    className='menu-item logout'
                    variants={menuItemVariants}
                    onClick={() => {
                      logout();
                      toggleMenu();
                    }}
                  >
                    <button className='btn link-button'>
                      <LogOut className='menu-item-icon' />
                      {t('menuLogout')}
                    </button>
                  </motion.div>
                </div>
              </>
            ) : (
              <div className='menu-group'>
                <div className='menu-group-title'>{t('prosMenuTitle')}</div>
                <motion.div className='menu-item' variants={menuItemVariants}>
                  <a
                    href='https://www.rintintin-pro.com'
                    target='_blank'
                    rel='noopener noreferrer'
                    onClick={toggleMenu}
                    className='external-link'
                  >
                    <Briefcase className='menu-item-icon' />
                    {t('iAmAPro')}
                  </a>
                </motion.div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
